import React, { useState } from "react";
import { motion } from "framer-motion"; // Import motion
import { MenuItemProps } from "./types";
import { getStorageUrl } from "../../utils/utils";
import { nodeColors, staticItems } from "./constants";
import { CornerDownLeft, Star, Layers } from "lucide-react"; // Import Star and Layers icons
import { MetaRouterIcon } from "@/config/icons";

export const MenuItem: React.FC<MenuItemProps> = ({
  item,
  isDestination = false,
  index,
  selectedIndex,
  onDragStart,
  onDrag,
  onDragEnd,
  onToggleFavorite, // Added prop
  isFavorite, // Added prop
  hideFavoriteIndicator, // New prop to hide star in FAV section
}) => {
  const [isHovered, setIsHovered] = useState(false);

  // Determine unique item ID for favorites
  const itemId = isDestination
    ? `destination-${(item as any).id}`
    : `${(item as any).id}-${(item as any).type}`;

  // Only show highlight if search input is focused
  const isSearchFocused = document.activeElement?.tagName === 'INPUT' && 
                         document.activeElement.getAttribute('type') === 'text';
  const isSelected = isSearchFocused && index === selectedIndex;
  
  if (isDestination) {
    // Cast item to Integration
    const integration = item as any;
    
    return (
      <button
        key={`destination-${integration.id}`}
        data-index={index}
        // Click to add is disabled, use drag instead
        // onClick={() => onAdd("destination", integration.name, integration)}
        draggable
        onDragStart={(e) => onDragStart(e, {
          nodeType: "destination",
          name: integration.name,
          type: integration.name,
          integration
        })}
        onDrag={onDrag}
        onDragEnd={onDragEnd}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        title="Drag to add to canvas"
        className={`w-full px-4 py-2 flex flex-row items-center gap-2 text-left cursor-grab
          transition-all rounded-md border border-transparent
          relative group // Add group class for hover effect on children
          ${isSelected && !isHovered
            ? "bg-gray-100 dark:bg-gray-700" 
            : "hover:bg-gray-50 dark:hover:bg-gray-700 hover:shadow-sm hover:border-gray-200 dark:hover:border-gray-600"
          }`}
      >
        <div className="w-8 h-8 rounded-lg flex-shrink-0">
          <img
            src={getStorageUrl(integration.logo_url || '')}
            alt={integration.name}
            className="w-full h-full object-contain"
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm text-gray-900 dark:text-gray-100 truncate">
            {integration.name}
          </div>
        </div>
        
        {isSelected && !isHovered && (
          <div className="text-gray-500 dark:text-gray-400">
            <CornerDownLeft className="h-4 w-4" />
          </div>
        )}
        {/* Favorite Star Icon - Conditionally render */}
        {!hideFavoriteIndicator && (
          <motion.div
            onClick={(e) => {
              e.stopPropagation(); // Prevent triggering drag/add
            onToggleFavorite(itemId);
          }}
          whileTap={{ scale: 1.3, rotate: 15 }}
          transition={{ duration: 0.1 }}
          className={`ml-auto p-1 rounded-full transition-all duration-150 cursor-pointer
            ${isFavorite
              ? 'text-gray-300 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800' // Light gray if favorite, visible
              : 'text-gray-400 dark:text-gray-500 opacity-0 group-hover:opacity-100 hover:text-yellow-500 dark:hover:text-yellow-400 hover:bg-yellow-100 dark:hover:bg-yellow-800' // Hidden unless hovered if not favorite, yellow on hover
            }`}
          title={isFavorite ? "Remove from favorites" : "Add to favorites"}
        >
          <Star
            className="h-4 w-4" // Removed explicit color classes, inherit from parent
            fill={isFavorite ? "currentColor" : "none"} // Fill based on favorite status
          />
          </motion.div>
        )}
      </button>
    );
  } else {
    // Cast item to StaticItemGroup
    const staticItem = item as any;
    const Icon = staticItem.icon; // Might be undefined

    // Get the appropriate colors from nodeColors
    const colorKey = staticItem.type || staticItem.id;
    const colors = nodeColors[colorKey] || nodeColors["source"];
    
    return (
      <button
        key={`${staticItem.id}-${staticItem.type}`}
        data-index={index}
        // Click to add is disabled, use drag instead
        // onClick={() => onAdd(staticItem.id as any, staticItem.name)}
        draggable
        onDragStart={(e) => onDragStart(e, {
          nodeType: staticItem.id as any,
          name: staticItem.name,
          type: staticItem.type,
          icon: staticItem.icon
        })}
        onDrag={onDrag}
        onDragEnd={onDragEnd}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        title="Drag to add to canvas"
        className={`px-3 py-1 flex flex-row min-w-40 items-center gap-2 rounded-md text-left cursor-grab
          transition-all duration-200 border border-transparent
          relative group // Add group class for hover effect on children
          ${isSelected && !isHovered
            ? "bg-gray-100 dark:bg-gray-700"
            : "hover:bg-gray-50 dark:hover:bg-gray-700 hover:shadow-sm hover:border-gray-200 dark:hover:border-gray-600"
          }`}
      >
        {/* Icon Container - Ensure Icon is rendered */}
        <div className={`w-8 h-8 ${staticItems.pipeline.some((pipelineItem: any) => pipelineItem.type === staticItem.type) ? 'rounded-md' : 'rounded-full'} ${colors.bg} ${colors.text} ${colors.bgDark} ${colors.textDark} flex items-center justify-center flex-shrink-0`}>
          {/* Handle different icon types */}
          {Icon === MetaRouterIcon ? 
            <MetaRouterIcon size={16} /> : 
            (Icon === Layers ? 
              <Layers className="h-4 w-4" /> : 
              (Icon && React.isValidElement(<Icon />) ? <Icon className="h-4 w-4" /> : null))}
        </div>
        {/* Item Name */}
        <span className="text-sm text-gray-900 dark:text-gray-100 truncate flex-1 mr-2">{staticItem.name}</span> {/* Added margin-right */}

        {isSelected && !isHovered && (
          <div className="ml-auto text-gray-500 dark:text-gray-400">
            <CornerDownLeft className="h-4 w-4" />
          </div>
        )}
        {/* Favorite Star Icon - Conditionally render */}
        {!hideFavoriteIndicator && (
          <motion.div
            onClick={(e) => {
              e.stopPropagation(); // Prevent triggering drag/add
            onToggleFavorite(itemId);
          }}
          whileTap={{ scale: 1.3, rotate: 15 }}
          transition={{ duration: 0.1 }}
          className={`ml-auto p-1 rounded-full transition-all duration-150 cursor-pointer
            ${isFavorite
              ? 'text-gray-300 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800' // Light gray if favorite, visible
              : 'text-gray-400 dark:text-gray-500 opacity-0 group-hover:opacity-100 hover:text-yellow-500 dark:hover:text-yellow-400 hover:bg-yellow-100 dark:hover:bg-yellow-800' // Hidden unless hovered if not favorite, yellow on hover
            }`}
          title={isFavorite ? "Remove from favorites" : "Add to favorites"}
        >
          <Star
            className="h-4 w-4" // Removed explicit color classes, inherit from parent
            // Fill based on favorite status
            fill={isFavorite ? "currentColor" : "none"}
          />
          </motion.div>
        )}
      </button>
    );
  }
};
